/**
 * IntegrationDetail — widok szczegółu JEDNEJ integracji ERP (master-detail).
 *
 * Wcześniej zakładki (Synchronizacja / Historia / Mapowania / Dokumenty / Instrukcja) były
 * GLOBALNE — każda pokazywała wszystkie integracje naraz, co myliło przy kilku integracjach
 * (np. Handel + FK). Teraz: lista integracji (master, `IntegrationsTab`) → wejście w jedną →
 * TU jej własne zakładki, zawężone do tej integracji.
 *
 * Zestaw zakładek zależy od typu ERP: „Dokumenty" (podgląd dokumentów księgowych) jest tylko
 * dla FK (`symfonia_fk`) — dla Handla zakładka się nie pokazuje.
 */

import { type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ArrowLeft,
  BookOpen,
  Building2,
  FileText,
  RefreshCw,
  Waypoints,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { Tabs, type TabsItem } from '@/components/Tabs';
import { api } from '@/lib/api';
import { useCompanies } from '@/lib/use-permissions';

import { ErpDocumentsTab } from './ErpDocumentsTab';
import { ErpGuideTab } from './ErpGuideTab';
import { ErpHistoryTab } from './ErpHistoryTab';
import { ErpMappingsTab } from './ErpMappingsTab';
import { ErpSyncTab } from './ErpSyncTab';
import { IntegrationQuickActions } from './IntegrationQuickActions';
import { type ErpIntegrationDto } from './IntegrationsTab';

function TabLabel({
  icon: Icon,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-4 w-4" />
      {children}
    </span>
  );
}

export function IntegrationDetail({
  integrationId,
  onBack,
}: {
  integrationId: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  const listQ = useQuery({
    queryKey: ['erp-integrations', 'detail'],
    queryFn: async () => {
      const res = await api.get<{ integrations: ErpIntegrationDto[] }>('/erp-integrations', {
        params: {},
      });
      return res.data.integrations;
    },
  });

  const integration = (listQ.data ?? []).find((it) => it.id === integrationId);

  // Integracja jest PER FIRMA — pokazujemy, której firmy dotyczy (companyId → nazwa).
  const companiesQ = useCompanies();
  const companyName =
    companiesQ.data?.companies.find((c) => c.id === integration?.companyId)?.legalName ?? null;

  const backButton = (
    <Button variant="ghost" onClick={onBack} data-testid="erp-detail-back">
      <span className="inline-flex items-center gap-1.5">
        <ArrowLeft className="h-4 w-4" />
        {t('tenant.settings.integrations.detail.back')}
      </span>
    </Button>
  );

  if (listQ.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!integration) {
    return (
      <div className="space-y-4">
        {backButton}
        <div className="rounded-lg border border-dashed border-border-secondary p-10 text-center text-sm text-text-secondary">
          {t('tenant.settings.integrations.detail.notFound')}
        </div>
      </div>
    );
  }

  const isFk = integration.erpSystem === 'symfonia_fk';

  const items: TabsItem[] = [
    {
      value: 'sync',
      label: (
        <TabLabel icon={RefreshCw}>{t('tenant.settings.integrations.screen.tabs.sync')}</TabLabel>
      ),
      content: <ErpSyncTab integrationId={integrationId} />,
    },
    {
      value: 'history',
      label: (
        <TabLabel icon={Activity}>{t('tenant.settings.integrations.screen.tabs.history')}</TabLabel>
      ),
      content: <ErpHistoryTab integrationId={integrationId} />,
    },
    {
      value: 'mappings',
      label: (
        <TabLabel icon={Waypoints}>
          {t('tenant.settings.integrations.screen.tabs.mappings')}
        </TabLabel>
      ),
      content: (
        <ErpMappingsTab
          integrationId={integrationId}
          erpSystem={integration.erpSystem}
          connectionMode={integration.connectionMode}
        />
      ),
    },
    // „Dokumenty" tylko dla FK — Handel nie ma dokumentów księgowych.
    ...(isFk
      ? [
          {
            value: 'documents',
            label: (
              <TabLabel icon={FileText}>
                {t('tenant.settings.integrations.screen.tabs.documents')}
              </TabLabel>
            ),
            content: (
              <ErpDocumentsTab
                integrationId={integrationId}
                erpSystem={integration.erpSystem}
                connectionMode={integration.connectionMode}
              />
            ),
          },
        ]
      : []),
    {
      value: 'guide',
      label: (
        <TabLabel icon={BookOpen}>{t('tenant.settings.integrations.screen.tabs.guide')}</TabLabel>
      ),
      content: <ErpGuideTab />,
    },
  ];

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          {backButton}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-semibold text-text-primary">{integration.name}</h2>
            {companyName && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-border-primary bg-bg-secondary px-2.5 py-0.5 text-xs font-medium text-text-secondary"
                title={t('tenant.settings.integrations.detail.companyLabel')}
                data-testid="erp-detail-company"
              >
                <Building2 className="h-3.5 w-3.5 text-text-tertiary" />
                {companyName}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-sm text-text-secondary">
            {t(`tenant.settings.integrations.detail.system.${isFk ? 'fk' : 'handel'}`)} ·{' '}
            {integration.webapiUrl}
          </p>
        </div>
        <div className="shrink-0">
          <IntegrationQuickActions integration={integration} />
        </div>
      </div>

      <Tabs idPrefix={`erp-integration-${integrationId}`} items={items} />
    </div>
  );
}
